import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import {
  ArrowLeftIcon,
  SparklesIcon,
  ArrowPathIcon,
  BookmarkIcon,
  LightBulbIcon,
  InformationCircleIcon,
} from '@heroicons/react/24/outline';

import { generateQuestions, refineQuestion, createQuestion } from '../../api/questionApi';
import Badge from '../../components/ui/Badge';
import Card from '../../components/ui/Card';
import Spinner from '../../components/ui/Spinner';
import EmptyState from '../../components/ui/EmptyState';

// ── Schemas ───────────────────────────────────────────────────────────────────
const generateSchema = yup.object({
  topic:       yup.string().trim().required('Topic is required'),
  focus_area:  yup.string().trim(),
  count:       yup.number().oneOf([5, 10, 15, 20]).required(),
  difficulty:  yup.string().oneOf(['easy', 'medium', 'hard', 'mixed']).required(),
});

const refineSchema = yup.object({
  original_question: yup.string().trim().required('Paste a question to refine'),
  instruction:       yup.string().trim().required('Provide refinement instruction'),
});

// ── Helpers ───────────────────────────────────────────────────────────────────
const TYPE_VARIANT = {
  text:     'primary',
  textarea: 'secondary',
  mcq:      'info',
  scale:    'warning',
  boolean:  'success',
};
const DIFF_VARIANT = {
  easy:   'success',
  medium: 'warning',
  hard:   'error',
};

// ── Generated Question Card ───────────────────────────────────────────────────
function GeneratedCard({ question, selected, onToggle }) {
  return (
    <div
      onClick={onToggle}
      className={clsx(
        'group cursor-pointer rounded-card border p-4 transition',
        selected
          ? 'border-primary-400 bg-primary-50 ring-2 ring-primary-200'
          : 'border-neutral-200 bg-white hover:border-neutral-300'
      )}
    >
      <div className="flex items-start gap-3">
        {/* Checkbox */}
        <div
          className={clsx(
            'mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded border-2 transition',
            selected
              ? 'border-primary-600 bg-primary-600'
              : 'border-neutral-300 bg-white group-hover:border-primary-400'
          )}
        >
          {selected && (
            <svg className="h-2.5 w-2.5 text-white" viewBox="0 0 10 10" fill="none">
              <path
                d="M1.5 5.5L4 8L8.5 2.5"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          )}
        </div>

        <div className="flex-1 min-w-0">
          <p className="text-sm text-neutral-800 leading-relaxed">{question.question_text}</p>
          <div className="mt-2 flex flex-wrap gap-1.5">
            <Badge variant={TYPE_VARIANT[question.question_type] ?? 'default'}>
              {question.question_type}
            </Badge>
            <Badge variant={DIFF_VARIANT[question.difficulty_level] ?? 'default'}>
              {question.difficulty_level}
            </Badge>
            {question.category_name && (
              <Badge variant="default">{question.category_name}</Badge>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────
export default function AIGeneratorPage() {
  const [generated, setGenerated]   = useState([]);
  const [selected, setSelected]     = useState(new Set());
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving]         = useState(false);

  const [refinedText, setRefinedText]   = useState('');
  const [refining, setRefining]         = useState(false);

  // ── Generate form ────────────────────────────────────────────────────────
  const {
    register: regGen,
    handleSubmit: handleGen,
    getValues: getGenValues,
    formState: { errors: genErrors },
  } = useForm({
    resolver: yupResolver(generateSchema),
    defaultValues: { topic: '', focus_area: '', count: 10, difficulty: 'mixed' },
  });

  // ── Refine form ──────────────────────────────────────────────────────────
  const {
    register: regRef,
    handleSubmit: handleRef,
    reset: resetRef,
    formState: { errors: refErrors },
  } = useForm({
    resolver: yupResolver(refineSchema),
    defaultValues: { original_question: '', instruction: '' },
  });

  const onGenerate = async (values) => {
    setGenerating(true);
    setGenerated([]);
    setSelected(new Set());
    try {
      const res = await generateQuestions({
        topic:      values.topic,
        focus_area: values.focus_area || undefined,
        count:      Number(values.count),
        difficulty: values.difficulty,
      });
      const questions = res.data.data ?? res.data ?? [];
      setGenerated(questions);
      if (questions.length === 0) {
        toast('No questions returned. Try a different topic.');
      } else {
        toast.success(`Generated ${questions.length} questions`);
      }
    } catch (err) {
      const msg = err?.response?.data?.message ?? 'Generation failed';
      if (err?.response?.status === 429) {
        toast.error('Rate limit reached. Try again in a few minutes.');
      } else {
        toast.error(msg);
      }
    } finally {
      setGenerating(false);
    }
  };

  const onRegenerate = () => {
    const values = getGenValues();
    onGenerate(values);
  };

  const toggleSelect = (idx) => {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(idx) ? next.delete(idx) : next.add(idx);
      return next;
    });
  };

  const selectAll = () => setSelected(new Set(generated.map((_, i) => i)));
  const clearAll  = () => setSelected(new Set());

  const saveSelected = async () => {
    if (selected.size === 0) {
      toast.error('Select at least one question');
      return;
    }
    setSaving(true);
    const toSave = Array.from(selected).map((i) => generated[i]);
    let saved = 0;
    let failed = 0;
    await Promise.all(
      toSave.map((q) =>
        createQuestion({
          question_text:    q.question_text,
          question_type:    q.question_type,
          difficulty_level: q.difficulty_level,
          category_id:      q.category_id ?? null,
          is_required:      false,
          tags:             q.tags ?? [],
        })
          .then(() => { saved++; })
          .catch(() => { failed++; })
      )
    );
    setSaving(false);
    if (failed === 0) {
      toast.success(`Saved ${saved} question${saved !== 1 ? 's' : ''} to library`);
      setSelected(new Set());
    } else {
      toast(`Saved ${saved}, failed ${failed}`, { icon: '⚠️' });
    }
  };

  const onRefine = async (values) => {
    setRefining(true);
    setRefinedText('');
    try {
      const res = await refineQuestion({
        question:    values.original_question,
        instruction: values.instruction,
      });
      const refined = res.data.data?.refined_question ?? res.data?.refined_question ?? '';
      setRefinedText(refined);
    } catch (err) {
      toast.error(err?.response?.data?.message ?? 'Refinement failed');
    } finally {
      setRefining(false);
    }
  };

  const saveRefined = async () => {
    if (!refinedText) return;
    try {
      await createQuestion({
        question_text:    refinedText,
        question_type:    'text',
        difficulty_level: 'medium',
        is_required:      false,
        tags:             [],
      });
      toast.success('Refined question saved to library');
      setRefinedText('');
      resetRef();
    } catch {
      toast.error('Failed to save refined question');
    }
  };

  return (
    <div className="min-h-screen bg-surface p-6 lg:p-8">
      {/* Header */}
      <div className="mb-6 flex items-center gap-4">
        <Link
          to="/questions"
          className="inline-flex items-center gap-1.5 rounded-button p-2 text-neutral-500 hover:bg-neutral-100 hover:text-neutral-700 transition"
        >
          <ArrowLeftIcon className="h-4 w-4" />
        </Link>
        <div>
          <h1 className="text-2xl font-heading font-bold text-neutral-900">
            AI Question Generator
          </h1>
          <p className="mt-0.5 text-sm text-neutral-500">
            Generate tailored assessment questions using AI
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* ── Left: Generator Form ── */}
        <div className="space-y-6">
          <Card title="Generator Settings">
            <form onSubmit={handleGen(onGenerate)} className="space-y-4">
              {/* Topic */}
              <div>
                <label className="mb-1 block text-sm font-medium text-neutral-700">
                  Topic <span className="text-error-500">*</span>
                </label>
                <input
                  {...regGen('topic')}
                  placeholder="e.g. startup validation, product-market fit"
                  className={clsx(
                    'w-full rounded-input border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-100',
                    genErrors.topic
                      ? 'border-error-400 focus:border-error-400'
                      : 'border-neutral-200 focus:border-primary-400'
                  )}
                />
                {genErrors.topic && (
                  <p className="mt-1 text-xs text-error-500">{genErrors.topic.message}</p>
                )}
              </div>

              {/* Focus Area */}
              <div>
                <label className="mb-1 block text-sm font-medium text-neutral-700">
                  Focus Area
                  <span className="ml-1 text-xs text-neutral-400">(optional)</span>
                </label>
                <input
                  {...regGen('focus_area')}
                  placeholder="e.g. market research, competitive analysis"
                  className="w-full rounded-input border border-neutral-200 px-3 py-2 text-sm focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-100"
                />
              </div>

              {/* Count + Difficulty row */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="mb-1 block text-sm font-medium text-neutral-700">Count</label>
                  <select
                    {...regGen('count')}
                    className="w-full rounded-input border border-neutral-200 px-3 py-2 text-sm focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-100"
                  >
                    {[5, 10, 15, 20].map((n) => (
                      <option key={n} value={n}>
                        {n} questions
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-neutral-700">
                    Difficulty
                  </label>
                  <select
                    {...regGen('difficulty')}
                    className="w-full rounded-input border border-neutral-200 px-3 py-2 text-sm focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-100"
                  >
                    <option value="easy">Easy</option>
                    <option value="medium">Medium</option>
                    <option value="hard">Hard</option>
                    <option value="mixed">Mixed</option>
                  </select>
                </div>
              </div>

              {/* Rate limit note */}
              <div className="flex items-start gap-2 rounded-input bg-accent-50 px-3 py-2">
                <InformationCircleIcon className="mt-0.5 h-4 w-4 shrink-0 text-accent-600" />
                <p className="text-xs text-accent-700">
                  Limited to 10 generations per hour
                </p>
              </div>

              <button
                type="submit"
                disabled={generating}
                className="inline-flex w-full items-center justify-center gap-2 rounded-button bg-primary-600 px-5 py-2.5 text-sm font-semibold text-white shadow-button hover:bg-primary-700 disabled:opacity-60 transition"
              >
                {generating ? (
                  <>
                    <Spinner size="sm" className="text-white" />
                    Generating...
                  </>
                ) : (
                  <>
                    <SparklesIcon className="h-4 w-4" />
                    Generate Questions
                  </>
                )}
              </button>
            </form>
          </Card>

          {/* ── Refine Panel ── */}
          <Card title="Refine a Question">
            <form onSubmit={handleRef(onRefine)} className="space-y-4">
              <div>
                <label className="mb-1 block text-sm font-medium text-neutral-700">
                  Original Question <span className="text-error-500">*</span>
                </label>
                <textarea
                  {...regRef('original_question')}
                  rows={2}
                  placeholder="Paste the question you want to refine..."
                  className={clsx(
                    'w-full rounded-input border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-100',
                    refErrors.original_question
                      ? 'border-error-400 focus:border-error-400'
                      : 'border-neutral-200 focus:border-primary-400'
                  )}
                />
                {refErrors.original_question && (
                  <p className="mt-1 text-xs text-error-500">
                    {refErrors.original_question.message}
                  </p>
                )}
              </div>

              <div>
                <label className="mb-1 block text-sm font-medium text-neutral-700">
                  Instruction <span className="text-error-500">*</span>
                </label>
                <input
                  {...regRef('instruction')}
                  placeholder='e.g. "make it more specific" or "simplify for beginners"'
                  className={clsx(
                    'w-full rounded-input border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-100',
                    refErrors.instruction
                      ? 'border-error-400 focus:border-error-400'
                      : 'border-neutral-200 focus:border-primary-400'
                  )}
                />
                {refErrors.instruction && (
                  <p className="mt-1 text-xs text-error-500">{refErrors.instruction.message}</p>
                )}
              </div>

              <button
                type="submit"
                disabled={refining}
                className="inline-flex w-full items-center justify-center gap-2 rounded-button border border-primary-300 bg-white px-4 py-2 text-sm font-medium text-primary-700 hover:bg-primary-50 disabled:opacity-60 transition"
              >
                {refining ? (
                  <>
                    <Spinner size="sm" className="text-primary-600" />
                    Refining...
                  </>
                ) : (
                  <>
                    <LightBulbIcon className="h-4 w-4" />
                    Refine Question
                  </>
                )}
              </button>

              {/* Refined result */}
              {refinedText && (
                <div className="rounded-card border border-secondary-200 bg-secondary-50 p-4">
                  <div className="mb-2 flex items-center justify-between">
                    <span className="text-xs font-semibold uppercase tracking-wide text-secondary-700">
                      Refined Version
                    </span>
                    <button
                      type="button"
                      onClick={saveRefined}
                      className="inline-flex items-center gap-1.5 rounded-button px-2.5 py-1 text-xs font-medium text-secondary-700 hover:bg-secondary-100 transition"
                    >
                      <BookmarkIcon className="h-3.5 w-3.5" />
                      Save to Library
                    </button>
                  </div>
                  <p className="text-sm text-neutral-800 leading-relaxed">{refinedText}</p>
                </div>
              )}
            </form>
          </Card>
        </div>

        {/* ── Right: Generated Questions Preview ── */}
        <div>
          <Card
            title="Generated Questions"
            subtitle={
              generated.length > 0
                ? `${generated.length} question${generated.length !== 1 ? 's' : ''} generated`
                : undefined
            }
            actions={
              generated.length > 0 ? (
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={onRegenerate}
                    disabled={generating}
                    className="inline-flex items-center gap-1.5 rounded-button p-1.5 text-neutral-500 hover:bg-neutral-100 disabled:opacity-50 transition"
                    title="Regenerate"
                  >
                    <ArrowPathIcon className={clsx('h-4 w-4', generating && 'animate-spin')} />
                  </button>
                </div>
              ) : null
            }
          >
            {generating ? (
              <div className="flex flex-col items-center justify-center py-16 gap-3">
                <Spinner size="lg" className="text-primary-600" />
                <p className="text-sm text-neutral-500">Generating questions with AI...</p>
              </div>
            ) : generated.length === 0 ? (
              <EmptyState
                icon={SparklesIcon}
                title="No questions yet"
                description="Fill in the generator form and click Generate to get started."
              />
            ) : (
              <div className="space-y-4">
                {/* Select all / clear bar */}
                <div className="flex items-center justify-between rounded-input bg-neutral-50 px-3 py-2">
                  <span className="text-xs text-neutral-600">
                    {selected.size} / {generated.length} selected
                  </span>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={selectAll}
                      className="text-xs text-primary-600 hover:text-primary-700 transition"
                    >
                      Select all
                    </button>
                    <span className="text-neutral-300">|</span>
                    <button
                      onClick={clearAll}
                      className="text-xs text-neutral-500 hover:text-neutral-700 transition"
                    >
                      Clear
                    </button>
                  </div>
                </div>

                {/* Question cards */}
                <div className="space-y-2 max-h-[560px] overflow-y-auto pr-1">
                  {generated.map((q, idx) => (
                    <GeneratedCard
                      key={idx}
                      question={q}
                      selected={selected.has(idx)}
                      onToggle={() => toggleSelect(idx)}
                    />
                  ))}
                </div>

                {/* Save button */}
                <button
                  onClick={saveSelected}
                  disabled={saving || selected.size === 0}
                  className="inline-flex w-full items-center justify-center gap-2 rounded-button bg-primary-600 px-5 py-2.5 text-sm font-semibold text-white shadow-button hover:bg-primary-700 disabled:opacity-60 transition"
                >
                  {saving ? (
                    <>
                      <Spinner size="sm" className="text-white" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <BookmarkIcon className="h-4 w-4" />
                      Save Selected to Library
                      {selected.size > 0 && (
                        <span className="ml-1 rounded-full bg-white/20 px-1.5 py-0.5 text-xs">
                          {selected.size}
                        </span>
                      )}
                    </>
                  )}
                </button>
              </div>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
}
